import { api } from 'shared/api';
import { Account, AccountUserMeta } from 'entities/account/model/types';
import { ApiResponse, EMI } from 'shared/model/types';
import { addAccounts, setAccounts, setMeta } from 'shared/config/store/reducers/accountsSlice';
import cardsApi from 'entities/card/api';
import userApi from 'entities/user/api';

const accountApi = api.injectEndpoints({
    endpoints: build => ({
        getVerificationToken: build.query<ApiResponse<{ token: string }>, { emi: EMI }>({
            query: ({ emi }) => ({
                url: 'private/river/client/verification-token',
                params: { emi },
            }),
        }),
        getAccounts: build.query<ApiResponse<Array<Account>>, { emi: EMI }>({
            query: ({ emi }) => {
                return {
                    url: '/private/river/get-accounts',
                    method: 'GET',
                    params: { emi }
                };
            },
            async onQueryStarted({ emi }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                const accounts = data?.response;
                if (accounts) {
                    dispatch(setAccounts({ emi, accounts: data.response }));

                    for (const account of accounts) {
                        await dispatch(cardsApi.endpoints.getAccountCards.initiate({
                            emi: emi,
                            accountId: account.id
                        }, { forceRefetch: true }));
                    }
                }
            },
            providesTags: ['accounts'],
        }),
        getAccountUserMeta: build.query<ApiResponse<AccountUserMeta>, { emi: EMI }>({
            query: ({ emi }) => {
                return {
                    url: '/private/river/client',
                    method: 'GET',
                    params: { emi },
                };
            },
            async onQueryStarted({ emi }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                if (data.response) dispatch(setMeta({ emi, meta: data.response }));
            }
        }),
        createPlanAccount: build.mutation<ApiResponse<Account>, ({ emi: EMI, title: string })>({
            query: ({ emi, title }) => ({
                url: `private/river/create-account`,
                method: 'POST',
                params: { title, emi }
            }),
            async onQueryStarted({ emi }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                if (data.response) dispatch(addAccounts({
                    emi, accounts: [{ ...data.response, available: 0 }]
                }));
            }
        }),
        createPlanMainAccount: build.mutation<unknown, { emi: EMI }>({
            query: ({ emi }) => {
                return {
                    url: 'private/river/create-private-client',
                    method: 'POST',
                    params: { emi }
                };
            },
            async onQueryStarted({ emi }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(
                        accountApi.endpoints.getAccountUserMeta.initiate({ emi }, { forceRefetch: true })
                    );
                    dispatch(
                        userApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true })
                    );
                } catch (error) {
                    console.error('Ошибка при выполнении createPlanMainAccount:', error);
                }
            },
        }),
        agreeTopUp: build.mutation<ApiResponse<AccountUserMeta>, { emi: EMI }>({
            query: ({ emi }) => ({
                url: '/private/river/client/agree-top-up',
                method: 'GET',
                params: { emi },
            }),
            async onQueryStarted({ emi }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                if (data.response) dispatch(setMeta({ emi, meta: data.response }));
            }
        }),
        accountPayment: build.mutation<ApiResponse<unknown>, {
            accountId: number;
            amount: number;
            corrAccountId: number;
            description?: string;
        }>({
            query: ({ accountId, amount, corrAccountId, description = '' }) => ({
                url: '/private/river/transfer',
                method: 'POST',
                body: {
                    accountId,
                    amount,
                    corrAccountId,
                    description: description || ' '
                },
            }),
            invalidatesTags: ['accounts', 'transactions'],
        }),
    })
});

export const {
    useGetAccountsQuery,
    useLazyGetAccountsQuery,
    useGetVerificationTokenQuery,
    useLazyGetAccountUserMetaQuery,
    useCreatePlanMainAccountMutation,
    useCreatePlanAccountMutation,
    useAgreeTopUpMutation,
    useAccountPaymentMutation
} = accountApi;

export default accountApi;
